import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  Send, Check, X, Loader2, HelpCircle, Inbox, Paperclip, Link2,
  AlertTriangle, Trash2, ChevronRight, ChevronLeft, Mail, Search, RotateCcw, Info,
} from 'lucide-react';

const FONT_UI = "'Inter',system-ui,sans-serif";

const CSS = `
  .fu-wrap{font-family:${FONT_UI};max-width:1480px;line-height:1.5}
  .fu-header{display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:20px;margin-bottom:20px}
  .fu-header-left{display:flex;align-items:flex-start;gap:14px}
  .fu-header-icon{width:44px;height:44px;border-radius:12px;background:#0A0A0A;display:flex;align-items:center;justify-content:center;color:#fff;flex-shrink:0}
  .fu-title-row{display:flex;align-items:center;gap:10px;margin-bottom:5px}
  .fu-title{font-size:24px;font-weight:700;color:#0A0A0A;letter-spacing:-0.015em}
  .fu-help-btn{display:inline-flex;align-items:center;gap:5px;font-size:12px;font-weight:600;color:#6B6B6B;background:#F5F5F5;border:1px solid #E5E5E5;border-radius:999px;padding:4px 11px 4px 9px;cursor:pointer;transition:background .12s,color .12s}
  .fu-help-btn:hover{background:#ECECEC;color:#0A0A0A}
  .fu-sub{font-size:14px;color:#6B6B6B;max-width:460px;line-height:1.55}

  .fu-toolbar{display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:16px;margin-bottom:28px}
  .fu-tabs{display:inline-flex;background:#F0F0F0;border-radius:11px;padding:3px;gap:2px}
  .fu-tab{font-family:${FONT_UI};font-size:13px;font-weight:600;color:#6B6B6B;background:none;border:none;border-radius:8px;padding:8px 16px;cursor:pointer;transition:background .12s,color .12s}
  .fu-tab--active{background:#fff;color:#0A0A0A;box-shadow:0 1px 2px rgba(0,0,0,.06)}
  .fu-status-pill{display:inline-flex;align-items:center;gap:7px;font-size:12.5px;font-weight:600;padding:8px 14px;border-radius:999px;cursor:pointer;border:none;transition:background .15s}
  .fu-status-pill--on{background:rgba(22,163,74,.1);color:#16A34A}
  .fu-status-pill--off{background:#F0F0F0;color:#9A9A9A}
  .fu-status-dot{width:7px;height:7px;border-radius:50%;background:currentColor}
  .fu-status-pill:disabled{opacity:.5;cursor:not-allowed}

  .fu-metrics-row{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:24px}
  .fu-metric-card{background:#fff;border:1px solid #E5E5E5;border-radius:14px;padding:18px 20px;transition:border-color .15s}
  .fu-metric-value{font-size:26px;font-weight:700;color:#0A0A0A;letter-spacing:-0.02em}
  .fu-metric-label{font-size:12px;color:#9A9A9A;margin-top:4px}

  .fu-sender-card{display:flex;align-items:center;justify-content:space-between;gap:20px;flex-wrap:wrap;background:#fff;border:1px solid #E5E5E5;border-radius:14px;padding:18px 22px;margin-bottom:24px}
  .fu-sender-left{display:flex;align-items:center;gap:14px}
  .fu-sender-icon{width:38px;height:38px;border-radius:10px;background:#F5F5F7;display:flex;align-items:center;justify-content:center;color:#0A0A0A;flex-shrink:0}
  .fu-sender-label{font-size:11px;color:#9A9A9A;margin-bottom:3px;display:flex;align-items:center;gap:5px}
  .fu-sender-name{font-size:14.5px;font-weight:700;color:#0A0A0A}
  .fu-sender-addr{font-size:12.5px;color:#6B6B6B;margin-top:1px}
  .fu-sender-reply{display:flex;align-items:center;gap:10px}
  .fu-sender-reply .fu-input{width:220px;padding:9px 12px;font-size:12.5px}
  .fu-info-tip{position:relative;display:inline-flex}
  .fu-info-tip svg{color:#B0B0B0;cursor:help}
  .fu-info-tip:hover .fu-tip-bubble{opacity:1;visibility:visible;transform:translateY(0)}
  .fu-tip-bubble{position:absolute;bottom:calc(100% + 8px);left:50%;transform:translateX(-50%) translateY(4px);width:230px;background:#0A0A0A;color:#fff;font-size:11.5px;line-height:1.5;padding:10px 12px;border-radius:8px;opacity:0;visibility:hidden;transition:opacity .12s,transform .12s;z-index:20;font-weight:400}

  .fu-timeline-card{background:#fff;border:1px solid #E5E5E5;border-radius:16px;padding:24px 28px 22px;margin-bottom:24px}
  .fu-timeline-title{font-size:15px;font-weight:700;color:#0A0A0A;margin-bottom:16px}
  .fu-timeline{display:flex;align-items:stretch;gap:0}
  .fu-timeline-step{flex:1;display:flex;flex-direction:column;align-items:flex-start;text-align:left;background:none;border:none;cursor:pointer;padding:14px 16px;border-radius:12px;transition:background .12s}
  .fu-timeline-step:hover{background:#FAFAFA}
  .fu-timeline-step--active{background:#F5F5F7}
  .fu-timeline-num{font-size:10.5px;font-weight:700;color:#B0B0B0;letter-spacing:.04em;margin-bottom:6px}
  .fu-timeline-name{font-size:14px;font-weight:700;color:#0A0A0A;margin-bottom:3px}
  .fu-timeline-when{font-size:12px;color:#9A9A9A;margin-bottom:8px}
  .fu-timeline-connector{width:44px;flex-shrink:0;display:flex;align-items:center;justify-content:center;color:#DCDCDC;margin-top:34px}
  .fu-timeline-foot{font-size:12px;color:#9A9A9A;margin-top:14px;padding-top:14px;border-top:1px solid #F0F0F0}

  .fu-step-status{display:inline-flex;align-items:center;gap:4px;font-size:10.5px;font-weight:600;padding:3px 9px;border-radius:999px}
  .fu-step-status--on{background:rgba(22,163,74,.1);color:#16A34A}
  .fu-step-status--off{background:#F0F0F0;color:#9A9A9A}
  .fu-step-status--pending{background:rgba(217,119,6,.1);color:#D97706}

  .fu-warning-banner{display:flex;align-items:flex-start;gap:12px;background:rgba(217,119,6,.06);border:1px solid rgba(217,119,6,.25);border-radius:12px;padding:14px 16px;margin-bottom:24px;font-size:12.5px;color:#92400E;line-height:1.55}
  .fu-warning-banner svg{flex-shrink:0;margin-top:1px;color:#D97706}

  .fu-grid{display:grid;grid-template-columns:1.5fr 1fr;gap:28px;align-items:start;margin-bottom:24px}
  .fu-card{background:#fff;border:1px solid #E5E5E5;border-radius:16px;padding:30px}
  .fu-card-title-row{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:4px}
  .fu-card-title{font-size:15px;font-weight:700;color:#0A0A0A;letter-spacing:-0.01em}
  .fu-card-desc{font-size:12.5px;color:#9A9A9A;margin-bottom:24px;line-height:1.5}
  .fu-save-state{font-size:11.5px;font-weight:600;display:inline-flex;align-items:center;gap:5px}
  .fu-save-state--saved{color:#16A34A}
  .fu-save-state--dirty{color:#D97706}

  .fu-field{margin-bottom:22px}
  .fu-field:last-of-type{margin-bottom:0}
  .fu-label{display:block;font-size:12.5px;font-weight:600;color:#525252;margin-bottom:8px}
  .fu-select,.fu-input{width:100%;font-family:${FONT_UI};font-size:13.5px;color:#0A0A0A;background:#fff;border:1px solid #DCDCDC;border-radius:10px;padding:11px 14px;outline:none;transition:border-color .15s}
  .fu-select:focus,.fu-input:focus,.fu-textarea:focus{border-color:#0A0A0A}
  .fu-input:disabled,.fu-select:disabled{background:#F5F5F5;color:#B0B0B0;cursor:not-allowed}
  .fu-textarea{width:100%;font-family:${FONT_UI};font-size:13.5px;color:#0A0A0A;background:#fff;border:1px solid #DCDCDC;border-radius:10px;padding:14px;outline:none;resize:vertical;min-height:220px;line-height:1.65}
  .fu-chips{display:flex;flex-wrap:wrap;gap:8px;margin-top:10px}
  .fu-chip{font-size:11.5px;font-weight:500;color:#525252;background:#F5F5F5;border:1px solid #E5E5E5;border-radius:999px;padding:6px 13px;cursor:pointer;transition:background .12s,color .12s}
  .fu-chip:hover{background:#ECECEC;color:#0A0A0A}
  .fu-restore-link{display:inline-flex;align-items:center;gap:5px;font-size:11.5px;font-weight:600;color:#9A9A9A;background:none;border:none;cursor:pointer;padding:0;margin-top:10px}
  .fu-restore-link:hover{color:#0A0A0A}

  .fu-toggle{position:relative;width:44px;height:26px;border-radius:999px;border:none;cursor:pointer;flex-shrink:0;transition:background .15s;padding:0}
  .fu-toggle--on{background:#16A34A}
  .fu-toggle--off{background:#E5E5E5}
  .fu-toggle-thumb{position:absolute;top:2px;left:2px;width:22px;height:22px;border-radius:50%;background:#fff;box-shadow:0 1px 3px rgba(0,0,0,.25);transition:transform .15s}
  .fu-toggle--on .fu-toggle-thumb{transform:translateX(18px)}
  .fu-toggle:disabled{opacity:.5;cursor:not-allowed}
  .fu-toggle-sm{width:34px;height:20px}
  .fu-toggle-sm .fu-toggle-thumb{width:16px;height:16px}
  .fu-toggle-sm.fu-toggle--on .fu-toggle-thumb{transform:translateX(14px)}

  .fu-attach-box{border:1.5px dashed #DCDCDC;border-radius:12px;padding:16px;text-align:center;cursor:pointer;transition:border-color .15s,background .15s}
  .fu-attach-box:hover{border-color:#0A0A0A;background:#FAFAFA}
  .fu-attach-box-text{font-size:12px;color:#9A9A9A;margin-top:6px}
  .fu-attach-chip{display:flex;align-items:center;gap:10px;background:#F5F5F7;border:1px solid #E5E5E5;border-radius:10px;padding:10px 14px}
  .fu-attach-chip-name{font-size:12.5px;font-weight:600;color:#0A0A0A;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .fu-attach-chip-size{font-size:11px;color:#9A9A9A}
  .fu-attach-remove{background:none;border:none;cursor:pointer;color:#9A9A9A;padding:4px;display:flex}
  .fu-attach-remove:hover{color:#DC2626}
  .fu-or-divider{display:flex;align-items:center;gap:10px;margin:14px 0;font-size:11px;color:#B0B0B0}
  .fu-or-divider:before,.fu-or-divider:after{content:'';flex:1;height:1px;background:#EEE}

  .fu-actions{display:flex;gap:12px;margin-top:28px;padding-top:24px;border-top:1px solid #F0F0F0;flex-wrap:wrap;align-items:center}
  .fu-btn{font-size:13px;font-weight:600;padding:12px 20px;border-radius:10px;cursor:pointer;border:none;font-family:${FONT_UI};display:inline-flex;align-items:center;gap:7px;transition:background .15s,opacity .15s}
  .fu-btn:disabled{opacity:.5;cursor:not-allowed}
  .fu-btn-primary{background:#0A0A0A;color:#fff}
  .fu-btn-primary:hover:not(:disabled){background:#1a1a1a}
  .fu-btn-secondary{background:#fff;color:#0A0A0A;border:1px solid #DCDCDC}
  .fu-btn-secondary:hover:not(:disabled){background:#FAFAFA}
  .fu-btn-secondary.fu-btn--ok{color:#16A34A;border-color:rgba(22,163,74,.3)}
  .fu-btn-secondary.fu-btn--err{color:#DC2626;border-color:rgba(220,38,38,.3)}
  .fu-btn-danger-text{background:none;border:none;color:#DC2626;font-family:${FONT_UI};font-size:13px;font-weight:600;cursor:pointer;padding:10px 4px}
  .fu-test-select{font-family:${FONT_UI};font-size:12.5px;color:#525252;background:#fff;border:1px solid #DCDCDC;border-radius:10px;padding:9px 10px;outline:none}

  .fu-preview{background:#F5F5F7;border-radius:14px;overflow:hidden;border:1px solid #E5E5E5;box-shadow:0 1px 2px rgba(0,0,0,.03)}
  .fu-preview-meta{background:#fff;padding:16px 18px;border-bottom:1px solid #E5E5E5;font-size:12px;color:#6B6B6B;line-height:1.85}
  .fu-preview-meta b{color:#0A0A0A;font-weight:600}
  .fu-preview-body{padding:24px 20px;font-size:13.5px;color:#0A0A0A;line-height:1.75;white-space:pre-wrap;min-height:220px}
  .fu-preview-attach{margin:0 20px 20px;display:flex;align-items:center;gap:8px;font-size:11.5px;color:#6B6B6B;background:#fff;border:1px solid #E5E5E5;border-radius:8px;padding:8px 12px}

  .fu-recent-row{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 0;border-bottom:1px solid #F0F0F0;cursor:pointer}
  .fu-recent-row:last-child{border-bottom:none;padding-bottom:0}
  .fu-recent-row:first-child{padding-top:0}
  .fu-recent-row:hover .fu-recent-name{color:#0A0A0A}
  .fu-recent-name{font-size:13.5px;font-weight:600;color:#0A0A0A}
  .fu-recent-summary{font-size:12px;color:#9A9A9A;margin-top:3px}
  .fu-recent-time{font-size:11.5px;color:#B0B0B0;white-space:nowrap}
  .fu-view-all{display:flex;align-items:center;gap:5px;font-size:12.5px;font-weight:600;color:#0A0A0A;background:none;border:none;cursor:pointer;padding:14px 0 0;margin-top:6px}

  .fu-activity-toolbar{display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap;margin-bottom:20px}
  .fu-search-box{position:relative;flex:1;min-width:220px;max-width:340px}
  .fu-search-box svg{position:absolute;left:13px;top:50%;transform:translateY(-50%);color:#B0B0B0}
  .fu-search-input{width:100%;font-family:${FONT_UI};font-size:13px;padding:10px 12px 10px 38px;border:1px solid #DCDCDC;border-radius:10px;outline:none;background:#fff}
  .fu-search-input:focus{border-color:#0A0A0A}
  .fu-filters{display:flex;gap:8px;flex-wrap:wrap}
  .fu-filter-pill{font-family:${FONT_UI};font-size:12px;font-weight:600;padding:7px 14px;border-radius:999px;border:1px solid #E5E5E5;background:#fff;color:#6B6B6B;cursor:pointer;transition:background .12s,color .12s}
  .fu-filter-pill--active{background:#0A0A0A;color:#fff;border-color:#0A0A0A}

  .fu-table{width:100%;border-collapse:collapse}
  .fu-table thead th{text-align:left;font-size:11px;font-weight:600;color:#9A9A9A;text-transform:uppercase;letter-spacing:.04em;padding:0 16px 12px;border-bottom:1px solid #E5E5E5}
  .fu-table tbody tr{cursor:pointer;transition:background .1s}
  .fu-table tbody tr:hover{background:#FAFAFA}
  .fu-table tbody td{padding:16px;border-bottom:1px solid #F0F0F0;font-size:13px;vertical-align:middle}
  .fu-table-name{font-weight:600;color:#0A0A0A}
  .fu-table-email{font-size:11.5px;color:#9A9A9A;margin-top:2px}
  .fu-table-muted{color:#9A9A9A}

  .fu-pagination{display:flex;align-items:center;justify-content:space-between;gap:16px;padding-top:18px;margin-top:6px}
  .fu-pagination-info{font-size:12.5px;color:#9A9A9A}
  .fu-pagination-btns{display:flex;gap:8px}
  .fu-page-btn{display:inline-flex;align-items:center;gap:5px;font-family:${FONT_UI};font-size:12.5px;font-weight:600;color:#0A0A0A;background:#fff;border:1px solid #DCDCDC;border-radius:9px;padding:8px 14px;cursor:pointer}
  .fu-page-btn:disabled{opacity:.4;cursor:not-allowed}

  .fu-activity-badge{font-size:11px;font-weight:600;padding:4px 11px;border-radius:999px;white-space:nowrap;display:inline-block}
  .fu-badge--scheduled{background:rgba(217,119,6,.1);color:#D97706}
  .fu-badge--sent,.fu-badge--delivered{background:rgba(22,163,74,.1);color:#16A34A}
  .fu-badge--failed,.fu-badge--bounced{background:rgba(220,38,38,.1);color:#DC2626}
  .fu-badge--cancelled{background:#F0F0F0;color:#9A9A9A}

  .fu-empty{padding:56px 20px;text-align:center}
  .fu-empty-icon{width:44px;height:44px;border-radius:50%;background:#F5F5F5;display:flex;align-items:center;justify-content:center;color:#B0B0B0;margin:0 auto 16px}
  .fu-empty-title{font-size:14px;font-weight:600;color:#0A0A0A;margin-bottom:6px}
  .fu-empty-text{font-size:13px;color:#9A9A9A;max-width:320px;margin:0 auto;line-height:1.6}

  .fu-loading{padding:80px 20px;display:flex;flex-direction:column;align-items:center;gap:14px;color:#B0B0B0}

  .fu-drawer-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.35);z-index:90;animation:fu-fade .15s ease}
  .fu-drawer{position:fixed;top:0;right:0;bottom:0;width:100%;max-width:420px;background:#fff;z-index:91;box-shadow:-8px 0 30px rgba(0,0,0,.12);display:flex;flex-direction:column;animation:fu-slide .18s ease}
  @keyframes fu-fade{from{opacity:0}to{opacity:1}}
  @keyframes fu-slide{from{transform:translateX(24px);opacity:.6}to{transform:translateX(0);opacity:1}}
  .fu-drawer-head{padding:26px 26px 20px;border-bottom:1px solid #F0F0F0;display:flex;justify-content:space-between;align-items:flex-start}
  .fu-drawer-close{background:#F5F5F5;border:none;border-radius:8px;width:30px;height:30px;display:flex;align-items:center;justify-content:center;cursor:pointer;color:#6B6B6B;flex-shrink:0}
  .fu-drawer-close:hover{background:#ECECEC;color:#0A0A0A}
  .fu-drawer-name{font-size:17px;font-weight:700;color:#0A0A0A}
  .fu-drawer-email{font-size:12.5px;color:#9A9A9A;margin-top:2px}
  .fu-drawer-body{flex:1;overflow-y:auto;padding:22px 26px}
  .fu-drawer-status-label{font-size:11px;font-weight:600;color:#9A9A9A;text-transform:uppercase;letter-spacing:.04em;margin-bottom:8px}
  .fu-drawer-steps{margin-top:22px}
  .fu-drawer-step{position:relative;padding:0 0 22px 26px}
  .fu-drawer-step:last-child{padding-bottom:0}
  .fu-drawer-step::before{content:'';position:absolute;left:5px;top:4px;width:9px;height:9px;border-radius:50%;background:#DCDCDC;border:2px solid #fff;box-shadow:0 0 0 1px #DCDCDC}
  .fu-drawer-step--done::before{background:#16A34A;box-shadow:0 0 0 1px #16A34A}
  .fu-drawer-step--cancelled::before{background:#DC2626;box-shadow:0 0 0 1px #DC2626}
  .fu-drawer-step::after{content:'';position:absolute;left:9px;top:14px;bottom:-8px;width:1px;background:#EEE}
  .fu-drawer-step:last-child::after{display:none}
  .fu-drawer-step-name{font-size:13.5px;font-weight:700;color:#0A0A0A}
  .fu-drawer-step-status{font-size:12px;color:#6B6B6B;margin-top:2px}
  .fu-drawer-step-note{font-size:11.5px;color:#9A9A9A;margin-top:2px}
  .fu-drawer-next{background:#F5F5F7;border-radius:12px;padding:16px;margin-top:22px}
  .fu-drawer-next-label{font-size:11px;font-weight:600;color:#9A9A9A;text-transform:uppercase;letter-spacing:.04em;margin-bottom:6px}
  .fu-drawer-next-value{font-size:13.5px;font-weight:700;color:#0A0A0A}
  .fu-drawer-next-time{font-size:12px;color:#6B6B6B;margin-top:2px}
  .fu-drawer-foot{padding:20px 26px;border-top:1px solid #F0F0F0}

  .fu-modal-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.45);backdrop-filter:blur(3px);z-index:100;display:flex;align-items:center;justify-content:center;padding:16px}
  .fu-modal-card{width:100%;max-width:440px;background:#fff;border-radius:20px;border:1px solid rgba(0,0,0,.08);box-shadow:0 12px 40px rgba(0,0,0,.22),0 2px 8px rgba(0,0,0,.1);padding:38px 34px 32px}
  .fu-modal-card--center{text-align:center}
  .fu-onboard-visual{display:flex;align-items:center;justify-content:center;gap:16px;margin-bottom:24px}
  .fu-onboard-icon-circle{width:52px;height:52px;border-radius:50%;background:#F5F5F7;display:flex;align-items:center;justify-content:center;color:#0A0A0A}
  .fu-onboard-title{font-size:19px;font-weight:700;color:#0A0A0A;margin-bottom:8px;letter-spacing:-0.015em;text-align:center}
  .fu-onboard-sub{font-size:13px;color:#9A9A9A;margin-bottom:24px;text-align:center}
  .fu-onboard-dots{display:flex;justify-content:center;gap:6px;margin-bottom:22px}
  .fu-onboard-dot{width:6px;height:6px;border-radius:50%;background:#E5E5E5;transition:background .15s,width .15s}
  .fu-onboard-dot--active{background:#0A0A0A;width:16px;border-radius:3px}
  .fu-onboard-block{background:#F5F5F7;border-radius:12px;padding:16px 18px;margin-bottom:12px;text-align:left}
  .fu-onboard-block-label{font-size:11px;color:#9A9A9A;margin-bottom:3px}
  .fu-onboard-block-value{font-size:13.5px;font-weight:700;color:#0A0A0A}
  .fu-onboard-explain{font-size:12.5px;color:#6B6B6B;line-height:1.6;text-align:left;margin-top:12px}
  .fu-onboard-mini-step{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 14px;background:#F5F5F7;border-radius:10px;margin-bottom:8px}
  .fu-onboard-mini-name{font-size:13px;font-weight:700;color:#0A0A0A}
  .fu-onboard-mini-when{font-size:11.5px;color:#9A9A9A;margin-top:1px}
  .fu-onboard-check{display:flex;align-items:center;gap:9px;font-size:13px;color:#0A0A0A;padding:6px 0}
  .fu-onboard-check svg{color:#16A34A;flex-shrink:0}
  .fu-onboard-actions{display:flex;flex-direction:column;gap:10px;margin-top:8px}
  .fu-onboard-skip{background:none;border:none;font-size:12.5px;color:#9A9A9A;cursor:pointer;padding:6px}
  .fu-onboard-skip:hover{color:#525252}

  .fu-modal-card--wide{max-width:920px;padding:40px 44px 34px;text-align:left}
  .fu-onboard-top{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;margin-bottom:8px}
  .fu-onboard-eyebrow{font-size:12px;font-weight:600;color:#9A9A9A;text-transform:uppercase;letter-spacing:.05em;margin-bottom:6px}
  .fu-onboard-h1{font-size:21px;font-weight:700;color:#0A0A0A;letter-spacing:-0.015em}
  .fu-onboard-h1-sub{font-size:13.5px;color:#6B6B6B;margin-top:6px;max-width:460px;line-height:1.55}
  .fu-onboard-close{background:#F5F5F5;border:none;border-radius:8px;width:30px;height:30px;display:flex;align-items:center;justify-content:center;cursor:pointer;color:#6B6B6B;flex-shrink:0}
  .fu-onboard-close:hover{background:#ECECEC;color:#0A0A0A}

  .fu-onboard-flow{display:flex;align-items:center;justify-content:center;gap:10px;margin:22px 0 6px;color:#6B6B6B;font-size:13px;font-weight:600}
  .fu-onboard-flow-arrow{color:#DCDCDC}
  .fu-onboard-flow-note{text-align:center;font-size:12.5px;color:#9A9A9A;margin-bottom:26px}

  .fu-path-grid{display:grid;grid-template-columns:1fr 1fr;gap:20px}
  .fu-path-card{border:1.5px solid #E5E5E5;border-radius:16px;padding:26px 24px;display:flex;flex-direction:column;gap:14px;transition:border-color .15s,box-shadow .15s}
  .fu-path-card--reco{border-color:#0A0A0A;box-shadow:0 2px 10px rgba(0,0,0,.06)}
  .fu-path-badge{display:inline-flex;align-self:flex-start;font-size:10.5px;font-weight:700;color:#fff;background:#0A0A0A;padding:4px 10px;border-radius:999px;letter-spacing:.02em}
  .fu-path-title{font-size:16px;font-weight:700;color:#0A0A0A}
  .fu-path-desc{font-size:12.5px;color:#6B6B6B;line-height:1.6;flex:1}
  .fu-path-visual{background:#F5F5F7;border-radius:12px;padding:14px 16px}
  .fu-path-visual-step{display:flex;align-items:center;justify-content:space-between;padding:6px 0}
  .fu-path-visual-name{font-size:12.5px;font-weight:700;color:#0A0A0A}
  .fu-path-visual-when{font-size:11px;color:#9A9A9A}
  .fu-path-cta{margin-top:auto}

  .fu-progress-steps{display:flex;align-items:center;gap:6px;margin:22px 0 26px}
  .fu-progress-seg{height:3px;flex:1;border-radius:3px;background:#EEE;transition:background .15s}
  .fu-progress-seg--done{background:#0A0A0A}

  .fu-wizard-body{min-height:260px}
  .fu-wizard-footer{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:28px;padding-top:20px;border-top:1px solid #F0F0F0}

  .fu-onboard-cols{display:grid;grid-template-columns:1.15fr 1fr;gap:32px;align-items:start}
  .fu-onboard-livepreview{background:#F5F5F7;border-radius:14px;border:1px solid #E5E5E5;overflow:hidden}
  .fu-sender-preview-row{display:flex;flex-direction:column;gap:2px;padding:14px 16px;font-size:12.5px;color:#6B6B6B}
  .fu-sender-preview-row b{color:#0A0A0A;font-weight:600}

  .fu-msg-tabs{display:flex;gap:4px;background:#F0F0F0;border-radius:10px;padding:3px;margin-bottom:20px}
  .fu-msg-tab{flex:1;font-family:${FONT_UI};font-size:12.5px;font-weight:600;color:#6B6B6B;background:none;border:none;border-radius:7px;padding:8px 10px;cursor:pointer;transition:background .12s,color .12s}
  .fu-msg-tab--active{background:#fff;color:#0A0A0A;box-shadow:0 1px 2px rgba(0,0,0,.06)}
  .fu-msg-tab-dot{display:inline-block;width:5px;height:5px;border-radius:50%;background:#16A34A;margin-left:5px}
  .fu-msg-actions{display:flex;gap:10px;margin-top:16px}

  .fu-success-icon{width:60px;height:60px;border-radius:50%;background:rgba(22,163,74,.1);color:#16A34A;display:flex;align-items:center;justify-content:center;margin:0 auto 20px}

  @media (max-width: 760px) {
    .fu-path-grid{grid-template-columns:1fr}
    .fu-onboard-cols{grid-template-columns:1fr}
    .fu-modal-card--wide{padding:28px 20px 24px}
  }

  .fu-howit-item{display:flex;gap:14px;margin-bottom:18px;text-align:left}
  .fu-howit-num{width:26px;height:26px;border-radius:50%;background:#0A0A0A;color:#fff;font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0}
  .fu-howit-title{font-size:13.5px;font-weight:700;color:#0A0A0A;margin-bottom:3px}
  .fu-howit-desc{font-size:12.5px;color:#6B6B6B;line-height:1.55}
  .fu-howit-note{font-size:11.5px;color:#9A9A9A;background:#F5F5F7;border-radius:10px;padding:12px 14px;line-height:1.55;margin-top:6px}

  @media (max-width: 1100px) {
    .fu-grid{grid-template-columns:1fr}
  }
  @media (max-width: 900px) {
    .fu-metrics-row{grid-template-columns:repeat(2,1fr)}
    .fu-header{flex-direction:column}
    .fu-timeline{flex-direction:column}
    .fu-timeline-connector{display:none}
    .fu-sender-card{flex-direction:column;align-items:flex-start}
    .fu-sender-reply{width:100%}
    .fu-sender-reply .fu-input{width:100%}
    .fu-table thead{display:none}
    .fu-table, .fu-table tbody, .fu-table tr, .fu-table td{display:block;width:100%}
    .fu-table tbody tr{border:1px solid #F0F0F0;border-radius:12px;margin-bottom:10px;padding:12px}
    .fu-table tbody td{border-bottom:none;padding:4px 4px}
    .fu-drawer{max-width:100%}
  }

  @keyframes fu-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
  .fu-spin{animation:fu-spin .8s linear infinite}
`;

const DELAY_LABELS = {
  0: 'Inmediatamente',
  15: '15 minutos',
  30: '30 minutos',
  60: '1 hora',
  180: '3 horas',
  1440: '1 día',
};

const FALLBACK_VARIABLES = [
  { token: 'nombre', label: 'Nombre del contacto' },
  { token: 'mi_nombre', label: 'Mi nombre' },
  { token: 'mi_nombre_completo', label: 'Mi nombre completo' },
  { token: 'mi_empresa', label: 'Mi empresa' },
  { token: 'mi_perfil', label: 'Mi perfil' },
];

const STEP_KEYS = ['connection', 'followup', 'value'];
const STEP_META = {
  connection: { num: 'Email 1', name: 'Conexión' },
  followup: { num: 'Email 2', name: 'Seguimiento' },
  value: { num: 'Email 3', name: 'Aporte de valor' },
};

const SEQUENCE_STATUS_LABELS = {
  active: 'En seguimiento',
  responded: 'Respondió',
  completed: 'Completado',
  finished: 'Completado',
  cancelled: 'Cancelado',
  failed: 'Fallido',
};
const SEQUENCE_BADGE_CLASS = {
  active: 'fu-badge--scheduled',
  responded: 'fu-badge--sent',
  completed: 'fu-badge--delivered',
  finished: 'fu-badge--delivered',
  cancelled: 'fu-badge--cancelled',
  failed: 'fu-badge--failed',
};
const STEP_STATUS_LABELS = {
  scheduled: 'Programado',
  sent: 'Enviado',
  delivered: 'Entregado',
  cancelled: 'Cancelado',
  skipped: 'Omitido',
  failed: 'Fallido',
  bounced: 'Rebotado',
};

const ACTIVITY_FILTERS = [
  { key: '', label: 'Todos' },
  { key: 'active', label: 'En seguimiento' },
  { key: 'responded', label: 'Respondieron' },
  { key: 'completed', label: 'Completados' },
  { key: 'cancelled', label: 'Cancelados' },
  { key: 'failed', label: 'Fallidos' },
];

const PAGE_SIZE = 20;

const stepName = (key, t) => t(`followups.step.${key}.name`, STEP_META[key].name);
const delayLabel = (minutes, t) => t(`followups.delay.${minutes}`, DELAY_LABELS[minutes] || `${minutes} min`);
const sequenceStatusLabel = (status, t) => t(`followups.seqStatus.${status}`, SEQUENCE_STATUS_LABELS[status] || status);
const stepStatusLabel = (status, t) => t(`followups.stepStatus.${status}`, STEP_STATUS_LABELS[status] || status);
const activityFilterLabel = (f, t) => t(`followups.filter.${f.key || 'all'}`, f.label);

const emptySteps = () => ({
  connection: { active: true, subject: '', message: '', resource_link: '', attachment: null },
  followup: { active: false, subject: '', message: '', resource_link: '', attachment: null },
  value: { active: false, subject: '', message: '', resource_link: '', attachment: null },
});

const renderPreview = (text, values) => (text || '').replace(/\{\{(\w+)\}\}/g, (_, key) => values[key] || '');

const formatBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const timeAgo = (iso, t) => {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return t('followups.timeAgo.now', 'ahora');
  if (mins < 60) return t('followups.timeAgo.minutes', `hace ${mins} min`).replace('{n}', mins);
  const hours = Math.round(mins / 60);
  if (hours < 24) return t('followups.timeAgo.hours', `hace ${hours} h`).replace('{n}', hours);
  const days = Math.round(hours / 24);
  return days === 1 ? t('followups.timeAgo.yesterday', 'ayer') : t('followups.timeAgo.days', `hace ${days} días`).replace('{n}', days);
};

const shortDate = (iso, locale = 'es-CL') => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(locale, { day: '2-digit', month: 'short' });
};

const fullDateTime = (iso, locale = 'es-CL') => {
  if (!iso) return '';
  return new Date(iso).toLocaleString(locale, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const normalizedStatus = (raw) => (raw === 'finished' ? 'completed' : raw);

const stepProgress = (steps, t) => {
  const real = STEP_KEYS.filter((k) => (steps?.[k]?.status) !== 'skipped');
  const done = real.filter((k) => ['sent', 'delivered'].includes(steps?.[k]?.status));
  return t('followups.stepProgress', `${done.length} de ${real.length || 1}`).replace('{done}', done.length).replace('{total}', real.length || 1);
};

const nextStepInfo = (steps, t, locale = 'es-CL') => {
  for (const key of STEP_KEYS) {
    const s = steps?.[key];
    if (s && s.status === 'scheduled') {
      return `${stepName(key, t)} · ${shortDate(s.scheduled_at, locale)}`;
    }
  }
  return '—';
};

const lastActivityAt = (followUp) => followUp?.updated_at || followUp?.created_at;

/* ─────────────────────────────────────────────────────────────────────── */

const FollowUpsPanel = ({ user }) => {
  const { toast, confirm } = useNotification();
  const { t, lang } = useLanguage();
  const dateLocale = lang === 'en' ? 'en-US' : 'es-CL';
  const getToken = () => localStorage.getItem('token');
  const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

  const [view, setView] = useState('sequence'); // 'sequence' | 'activity'
  const [loading, setLoading] = useState(true);
  // If the initial settings fetch fails, onboarding's own trigger (inside
  // applySettings) never runs — without this, that fetch failure would
  // fall straight through to the normal editable panel below, bypassing
  // onboarding entirely for whoever hits a flaky request on first load.
  const [loadError, setLoadError] = useState(false);
  const [active, setActive] = useState(false);
  // Tracks whether the mandatory onboarding has ever been finished — closing
  // it early via "Terminar más tarde" leaves this false, and the main
  // panel's own "Activo/Pausado" toggle must respect that (it previously
  // didn't check this at all, so it could turn Follow-ups on before the
  // sender identity / reply-to / messages were ever actually set up).
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [delayMinutes, setDelayMinutes] = useState(15);
  const [step2Days, setStep2Days] = useState(3);
  const [step3Days, setStep3Days] = useState(6);
  const [steps, setSteps] = useState(emptySteps());
  const [replyToEmail, setReplyToEmail] = useState('');
  const [configured, setConfigured] = useState(true);
  const [replyDetectionConfigured, setReplyDetectionConfigured] = useState(false);
  const [senderEmail, setSenderEmail] = useState('');
  const [variables, setVariables] = useState(FALLBACK_VARIABLES);
  const [valueVariables, setValueVariables] = useState(FALLBACK_VARIABLES);

  const [selectedStep, setSelectedStep] = useState('connection');
  // onboardPhase: null (hidden) | 'landing' | 'recommended' | 'custom' | 'success'
  const [onboardPhase, setOnboardPhase] = useState(null);
  const [onboardCustomStep, setOnboardCustomStep] = useState(1); // 1-4, only while onboardPhase === 'custom'
  const [onboardMsgTab, setOnboardMsgTab] = useState('connection');
  const [onboardCustomizing, setOnboardCustomizing] = useState({ connection: false, followup: false, value: false });
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [testStep, setTestStep] = useState('connection');
  const [testState, setTestState] = useState('idle'); // idle | sending | sent | error
  const [uploading, setUploading] = useState(false);

  const [metrics, setMetrics] = useState({ iniciadas: 0, respuestas: 0, en_seguimiento: 0, finalizados: 0, tasa_respuesta: 0 });
  const [recentActivity, setRecentActivity] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);

  const [drawerItem, setDrawerItem] = useState(null);

  const messageRef = useRef(null);
  const subjectRef = useRef(null);
  const fileInputRef = useRef(null);
  const initialOnboardCheckDone = useRef(false);

  const applySettings = (d) => {
    setActive(!!d.active);
    setOnboardingCompleted(!!d.onboarding_completed);
    setDelayMinutes(d.delay_minutes ?? 15);
    setStep2Days(d.step2_delay_days ?? 3);
    setStep3Days(d.step3_delay_days ?? 6);
    setReplyToEmail(d.reply_to_real_email || '');
    setConfigured(!!d.configured);
    setReplyDetectionConfigured(!!d.reply_detection_configured);
    setSenderEmail(d.sender_email || '');
    if (Array.isArray(d.variables) && d.variables.length) setVariables(d.variables);
    if (Array.isArray(d.value_variables) && d.value_variables.length) setValueVariables(d.value_variables);
    const merged = emptySteps();
    STEP_KEYS.forEach((key) => {
      const s = (d.steps || {})[key];
      if (s) {
        merged[key] = {
          active: !!s.active,
          subject: s.subject || '',
          message: s.message || '',
          resource_link: s.resource_link || '',
          attachment: s.attachment || null,
        };
      }
    });
    setSteps(merged);
    setDirty(false);
    // Only the very first load may auto-open onboarding — later persist()
    // calls (saving from the main editor, or saving draft progress on
    // onboarding exit) must never re-pop it back open mid-session.
    if (!initialOnboardCheckDone.current) {
      initialOnboardCheckDone.current = true;
      if (!d.onboarding_completed) setOnboardPhase('landing');
    }
  };

  const loadSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/follow-ups/settings`, { headers: authHeaders() });
      applySettings(res.data);
      setLoadError(false);
    } catch (e) {
      toast?.error?.(t('followups.toast.loadError', 'No pudimos cargar la configuración de Follow-ups.'));
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRecentActivity = useCallback(async () => {
    setRecentLoading(true);
    try {
      const [actRes, metRes] = await Promise.all([
        axios.get(`${API}/follow-ups/activity?limit=5`, { headers: authHeaders() }),
        axios.get(`${API}/follow-ups/metrics`, { headers: authHeaders() }),
      ]);
      setRecentActivity(actRes.data?.items || []);
      setMetrics(metRes.data || { iniciadas: 0, respuestas: 0, en_seguimiento: 0, finalizados: 0, tasa_respuesta: 0 });
    } catch (e) {
      // Non-critical — the config half of the page still works without it.
    } finally {
      setRecentLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); loadRecentActivity(); }, [loadSettings, loadRecentActivity]);

  const persist = async (patch) => {
    const res = await axios.put(`${API}/follow-ups/settings`, patch, { headers: authHeaders() });
    applySettings(res.data);
    return res.data;
  };

  const handleToggleActive = async () => {
    const next = !active;
    if (next && !onboardingCompleted) {
      toast?.error?.(t('followups.toast.needOnboarding', 'Completa la configuración inicial para activar los Follow-ups.'));
      setOnboardPhase('landing');
      return;
    }
    setActive(next);
    try {
      await persist({ active: next });
      toast?.success?.(next ? t('followups.toast.activated', 'Follow-up automático activado.') : t('followups.toast.paused', 'Follow-up automático pausado.'));
    } catch (e) {
      setActive(!next);
      toast?.error?.(t('followups.toast.statusError', 'No pudimos actualizar el estado.'));
    }
  };

  const handleStepField = (key, field, value) => {
    setSteps((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
    setDirty(true);
  };

  const handleStepActiveToggle = async (key) => {
    const next = !steps[key].active;
    const previous = steps[key].active;
    setSteps((prev) => ({ ...prev, [key]: { ...prev[key], active: next } }));
    try {
      const patch = {};
      STEP_KEYS.forEach((k) => {
        const s = k === key ? { ...steps[k], active: next } : steps[k];
        patch[k] = {
          active: s.active,
          subject: s.subject,
          message: s.message,
          resource_link: s.resource_link || null,
        };
      });
      await persist({ steps: patch });
      toast?.success?.(next
        ? t('followups.toast.stepActivated', `${STEP_META[key].name} activado.`).replace('{step}', stepName(key, t))
        : t('followups.toast.stepDeactivated', `${STEP_META[key].name} desactivado.`).replace('{step}', stepName(key, t)));
    } catch (e) {
      setSteps((prev) => ({ ...prev, [key]: { ...prev[key], active: previous } }));
      toast?.error?.(e?.response?.data?.detail || t('followups.toast.stepUpdateError', 'No pudimos actualizar el paso.'));
    }
  };

  const buildStepsPatch = () => {
    const patch = {};
    STEP_KEYS.forEach((key) => {
      patch[key] = {
        active: steps[key].active,
        subject: steps[key].subject,
        message: steps[key].message,
        resource_link: steps[key].resource_link || null,
      };
    });
    return patch;
  };

  const buildFullPatch = () => ({
    delay_minutes: delayMinutes,
    step2_delay_days: step2Days,
    step3_delay_days: step3Days,
    reply_to_real_email: replyToEmail || null,
    steps: buildStepsPatch(),
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await persist(buildFullPatch());
      toast?.success?.(t('followups.toast.saved', 'Cambios guardados.'));
    } catch (e) {
      toast?.error?.(e?.response?.data?.detail || t('followups.toast.saveError', 'No pudimos guardar los cambios.'));
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreDefault = async (key) => {
    const ok = await confirm(t('followups.confirm.restoreMessage', `¿Restaurar el mensaje recomendado de ${STEP_META[key].name}? Se reemplaza el asunto y el mensaje actuales.`).replace('{step}', stepName(key, t)), {
      title: t('followups.confirm.restoreMessageTitle', 'Restaurar mensaje recomendado'),
      confirmLabel: t('followups.confirm.restoreMessageConfirm', 'Restaurar'),
    });
    if (!ok) return;
    try {
      const res = await axios.post(`${API}/follow-ups/steps/${key}/restore-default`, {}, { headers: authHeaders() });
      if (res.data?.subject !== undefined) {
        handleStepField(key, 'subject', res.data.subject);
        handleStepField(key, 'message', res.data.message);
        toast?.success?.(t('followups.toast.restored', 'Mensaje recomendado restaurado — recordá guardar los cambios.'));
      }
    } catch (e) {
      toast?.error?.(t('followups.toast.restoreError', 'No pudimos restaurar el mensaje recomendado.'));
    }
  };

  const handleSendTest = async () => {
    setTestState('sending');
    try {
      await axios.post(`${API}/follow-ups/test-send?step=${testStep}`, {}, { headers: authHeaders() });
      setTestState('sent');
      toast?.success?.(t('followups.toast.testSent', 'Correo de prueba enviado — revisá tu bandeja de entrada.'));
      setTimeout(() => setTestState('idle'), 3000);
    } catch (e) {
      setTestState('error');
      toast?.error?.(e?.response?.data?.detail || t('followups.toast.testError', 'No pudimos enviar la prueba.'));
      setTimeout(() => setTestState('idle'), 3000);
    }
  };

  const insertVariable = (targetRef, key, field, token) => {
    const el = targetRef.current;
    const insertion = `{{${token}}}`;
    const current = steps[key][field];
    if (!el) { handleStepField(key, field, `${current}${insertion}`); return; }
    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    const nextValue = current.slice(0, start) + insertion + current.slice(end);
    handleStepField(key, field, nextValue);
    requestAnimationFrame(() => {
      el.focus();
      const cursor = start + insertion.length;
      el.setSelectionRange(cursor, cursor);
    });
  };

  const handleAttachmentPick = () => fileInputRef.current?.click();

  const handleAttachmentChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast?.error?.(t('followups.toast.fileTooBig', 'El archivo no puede superar 8MB.'));
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/follow-ups/steps/value/attachment`, formData, { headers: authHeaders() });
      handleStepField('value', 'attachment', res.data);
      handleStepField('value', 'resource_link', '');
      toast?.success?.(t('followups.toast.attached', 'Archivo adjuntado.'));
    } catch (e2) {
      toast?.error?.(e2?.response?.data?.detail || t('followups.toast.attachError', 'No pudimos subir el archivo.'));
    } finally {
      setUploading(false);
    }
  };

  const handleAttachmentRemove = async () => {
    setUploading(true);
    try {
      await axios.delete(`${API}/follow-ups/steps/value/attachment`, { headers: authHeaders() });
      handleStepField('value', 'attachment', null);
      toast?.success?.(t('followups.toast.attachRemoved', 'Adjunto eliminado.'));
    } catch (e) {
      toast?.error?.(t('followups.toast.attachRemoveError', 'No pudimos eliminar el adjunto.'));
    } finally {
      setUploading(false);
    }
  };

  const handleStopSequence = async (item) => {
    const ok = await confirm(t('followups.confirm.stopSequence', `¿Detener el seguimiento de ${item.full_name || item.email}? Los próximos correos programados serán cancelados.`).replace('{name}', item.full_name || item.email), {
      title: t('followups.confirm.stopSequenceTitle', 'Detener seguimiento'),
      confirmLabel: t('followups.confirm.stopSequenceConfirm', 'Detener seguimiento'),
      danger: true,
    });
    if (!ok) return;
    try {
      await axios.post(`${API}/follow-ups/${item.id}/cancel`, {}, { headers: authHeaders() });
      toast?.success?.(t('followups.toast.sequenceStopped', 'Seguimiento detenido.'));
      setDrawerItem(null);
      loadRecentActivity();
    } catch (e) {
      toast?.error?.(e?.response?.data?.detail || t('followups.toast.sequenceStopError', 'No pudimos detener el seguimiento.'));
    }
  };

  const startRecommendedOnboarding = () => {
    setSteps((prev) => ({
      ...prev,
      followup: { ...prev.followup, active: true },
      value: { ...prev.value, active: true },
    }));
    setOnboardPhase('recommended');
  };
  const startCustomOnboarding = () => {
    setOnboardCustomStep(1);
    setOnboardMsgTab('connection');
    setOnboardCustomizing({ connection: false, followup: false, value: false });
    setOnboardPhase('custom');
  };
  const handleOnboardCustomNext = () => setOnboardCustomStep((s) => Math.min(s + 1, 4));
  const handleOnboardCustomBack = () => setOnboardCustomStep((s) => Math.max(s - 1, 1));

  const activateFromOnboarding = async () => {
    try {
      await persist({ ...buildFullPatch(), active: true, onboarding_completed: true });
      setOnboardPhase('success');
      loadRecentActivity();
    } catch (e) {
      toast?.error?.('No pudimos activar Follow-ups.');
    }
  };

  const firstName = (user?.name || '').split(' ')[0] || t('followups.preview.youLower', 'tú');
  const previewValues = {
    nombre: 'Martín',
    mi_nombre: firstName,
    mi_nombre_completo: user?.name || t('followups.preview.yourName', 'Tu nombre'),
    mi_empresa: user?.team_name || '',
    mi_perfil: t('followups.preview.profileUrl', 'contactnetwork.cl/pages/tu-perfil'),
    recurso: steps.value.attachment ? steps.value.attachment.filename : (steps.value.resource_link || ''),
  };

  const stepStatusPill = (key) => {
    if (!steps[key].active) return { cls: 'fu-step-status--off', text: t('followups.stepPill.inactive', 'Inactivo') };
    if (key !== 'connection' && !replyDetectionConfigured) return { cls: 'fu-step-status--pending', text: t('followups.stepPill.pending', 'Pendiente config.') };
    return { cls: 'fu-step-status--on', text: t('followups.stepPill.active', 'Activo') };
  };

  const stepWhen = (key) => {
    if (key === 'connection') return delayLabel(delayMinutes, t);
    if (key === 'followup') return t('followups.stepWhen.day', `Día ${step2Days} si no responde`).replace('{n}', step2Days);
    return t('followups.stepWhen.day', `Día ${step3Days} si no responde`).replace('{n}', step3Days);
  };

  const showReplyWarning = (steps.followup.active || steps.value.active) && !replyDetectionConfigured;
  const activeStepVars = selectedStep === 'value' ? valueVariables : variables;
  const currentStep = steps[selectedStep];
  const responseRateLabel = lang === 'en'
    ? `${metrics.tasa_respuesta ?? 0}%`
    : `${String(metrics.tasa_respuesta ?? 0).replace('.', ',')}%`;

  if (loading) {
    return (
      <div className="fu-wrap">
        <style>{CSS}</style>
        <div className="fu-loading">
          <Loader2 size={22} className="fu-spin" />
          <span style={{ fontSize: 13 }}>{t('followups.loading', 'Cargando Follow-ups…')}</span>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="fu-wrap">
        <style>{CSS}</style>
        <div className="fu-loading">
          <span style={{ fontSize: 13, color: '#6B6B6B' }}>{t('followups.loadErrorMsg', 'No pudimos cargar tu configuración de Follow-ups.')}</span>
          <button className="fu-btn fu-btn-primary" style={{ marginTop: 12 }} onClick={loadSettings}>{t('followups.retry', 'Reintentar')}</button>
        </div>
      </div>
    );
  }

  const customStepTitles = [
    t('followups.custom.step1.title', 'Haz que tus correos se sientan tuyos'),
    t('followups.custom.step2.title', 'Define cómo quieres hacer seguimiento'),
    t('followups.custom.step3.title', 'Prepara tus mensajes'),
    t('followups.custom.step4.title', 'Todo está listo'),
  ];
  const customStepSubs = [
    t('followups.custom.step1.sub', 'Así te verán las personas cuando reciban tus seguimientos.'),
    t('followups.custom.step2.sub', 'Elige cuándo CONTACT debe volver a escribir si todavía no recibiste respuesta.'),
    t('followups.custom.step3.sub', 'Puedes empezar con nuestros mensajes recomendados y editarlos cuando quieras.'),
    t('followups.custom.step4.sub', 'Revisa tu configuración antes de activar Follow-ups.'),
  ];

  const renderCustomStep = () => {
    if (onboardCustomStep === 1) {
      return (
        <div className="fu-onboard-cols">
          <div>
            <div className="fu-onboard-block">
              <div className="fu-onboard-block-label">{t('followups.custom.name', 'Nombre')}</div>
              <div className="fu-onboard-block-value">{user?.name || t('followups.preview.yourName', 'Tu nombre')}</div>
            </div>
            <div className="fu-onboard-block">
              <div className="fu-onboard-block-label">{t('followups.custom.sentAs', 'Tus correos se enviarán como')}</div>
              <div className="fu-onboard-block-value">{user?.name || t('followups.preview.you', 'Tú')}</div>
              <div style={{ fontSize: 12.5, color: '#6B6B6B', marginTop: 2 }}>{senderEmail || '—'}</div>
            </div>
            <div className="fu-onboard-block">
              <div className="fu-onboard-block-label">{t('followups.custom.repliesTo', 'Las respuestas llegarán directamente a')}</div>
              <input
                className="fu-input"
                style={{ marginTop: 4 }}
                value={replyToEmail}
                onChange={(e) => { setReplyToEmail(e.target.value); setDirty(true); }}
                placeholder={user?.email || t('followups.custom.emailPlaceholder', 'tu@correo.com')}
              />
            </div>
            <div className="fu-onboard-explain">{t('followups.custom.step1.explain', 'Las personas podrán responderte directamente desde su correo. Si usás otra dirección para leer tus correos, podés cambiarla acá.')}</div>
          </div>
          <div>
            <div className="fu-onboard-livepreview">
              <div className="fu-sender-preview-row">
                <div><b>{t('followups.preview.from', 'De:')}</b> {user?.name || t('followups.preview.you', 'Tú')} {senderEmail ? `<${senderEmail}>` : ''}</div>
                <div><b>{t('followups.preview.to', 'Para:')}</b> Martín Pérez</div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    if (onboardCustomStep === 2) {
      return (
        <>
          <div className="fu-timeline" style={{ marginBottom: 8 }}>
            {STEP_KEYS.map((key, i) => (
              <React.Fragment key={key}>
                <div className="fu-timeline-step" style={{ cursor: 'default' }}>
                  <span className="fu-timeline-num">{STEP_META[key].num}</span>
                  <span className="fu-timeline-name">{stepName(key, t)}</span>
                  {key === 'connection' ? (
                    <select className="fu-select" style={{ marginTop: 8 }} value={delayMinutes} onChange={(e) => { setDelayMinutes(Number(e.target.value)); setDirty(true); }}>
                      {Object.keys(DELAY_LABELS).map((val) => <option key={val} value={val}>{delayLabel(Number(val), t)}</option>)}
                    </select>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                      <input
                        type="number" min={1} max={30} className="fu-input" style={{ width: 70 }}
                        value={key === 'followup' ? step2Days : step3Days}
                        onChange={(e) => {
                          if (key === 'followup') setStep2Days(Number(e.target.value)); else setStep3Days(Number(e.target.value));
                          setDirty(true);
                        }}
                      />
                      <span style={{ fontSize: 12, color: '#9A9A9A' }}>{t('followups.custom.daysAfter', 'días después')}</span>
                    </div>
                  )}
                  {key !== 'connection' && (
                    <button
                      className={`fu-toggle fu-toggle-sm fu-timeline-inline-toggle ${steps[key].active ? 'fu-toggle--on' : 'fu-toggle--off'}`}
                      style={{ marginTop: 10 }}
                      onClick={() => handleStepActiveToggle(key)}
                      aria-label={`${steps[key].active ? t('followups.custom.deactivate', 'Desactivar') : t('followups.custom.activate', 'Activar')} ${stepName(key, t)}`}
                    >
                      <span className="fu-toggle-thumb" />
                    </button>
                  )}
                </div>
                {i < STEP_KEYS.length - 1 && <div className="fu-timeline-connector"><ChevronRight size={16} /></div>}
              </React.Fragment>
            ))}
          </div>
          <div className="fu-onboard-explain">{t('followups.custom.step2.explain', 'Si la persona responde en cualquier momento, CONTACT cancela automáticamente los siguientes correos.')}</div>
        </>
      );
    }
    if (onboardCustomStep === 3) {
      const key = onboardMsgTab;
      const stepData = steps[key];
      const editing = onboardCustomizing[key];
      return (
        <>
          <div className="fu-msg-tabs">
            {STEP_KEYS.map((k) => (
              <button key={k} className={`fu-msg-tab ${onboardMsgTab === k ? 'fu-msg-tab--active' : ''}`} onClick={() => setOnboardMsgTab(k)}>
                {stepName(k, t)}{k !== 'connection' && steps[k].active ? <span className="fu-msg-tab-dot" /> : null}
              </button>
            ))}
          </div>
          {editing ? (
            <>
              <div className="fu-field">
                <label className="fu-label">{t('followups.field.subject', 'Asunto')}</label>
                <input className="fu-input" value={stepData.subject} onChange={(e) => handleStepField(key, 'subject', e.target.value)} />
              </div>
              <div className="fu-field">
                <label className="fu-label">{t('followups.field.message', 'Mensaje')}</label>
                <textarea className="fu-textarea" style={{ minHeight: 180 }} value={stepData.message} onChange={(e) => handleStepField(key, 'message', e.target.value)} />
              </div>
            </>
          ) : (
            <>
              <div className="fu-onboard-block">
                <div className="fu-onboard-block-label">{t('followups.field.subject', 'Asunto')}</div>
                <div className="fu-onboard-block-value" style={{ fontSize: 13, fontWeight: 500 }}>{renderPreview(stepData.subject, previewValues)}</div>
              </div>
              <div style={{ fontSize: 12.5, color: '#6B6B6B', lineHeight: 1.65, whiteSpace: 'pre-wrap', margin: '10px 2px 4px' }}>
                {renderPreview(stepData.message, previewValues)}
              </div>
            </>
          )}
          <div className="fu-msg-actions">
            <button
              className={`fu-btn ${editing ? 'fu-btn-secondary' : 'fu-btn-primary'}`}
              onClick={() => { if (editing) handleRestoreDefault(key); setOnboardCustomizing((p) => ({ ...p, [key]: false })); }}
            >
              {t('followups.custom.useRecommended', 'Usar recomendado')}
            </button>
            <button
              className={`fu-btn ${editing ? 'fu-btn-primary' : 'fu-btn-secondary'}`}
              onClick={() => setOnboardCustomizing((p) => ({ ...p, [key]: true }))}
            >
              {t('followups.custom.customize', 'Personalizar')}
            </button>
          </div>
        </>
      );
    }
    return (
      <>
        <div className="fu-onboard-check"><Check size={15} /> {t('followups.custom.check.identity', 'Identidad configurada —')} {user?.name || t('followups.preview.yourName', 'Tu nombre')}</div>
        <div className="fu-onboard-check"><Check size={15} /> {t('followups.custom.check.sentFrom', 'Tus correos se enviarán desde')} {senderEmail || '—'}</div>
        <div className="fu-onboard-check"><Check size={15} /> {t('followups.custom.check.repliesTo', 'Las respuestas llegarán a')} {replyToEmail || user?.email || '—'}</div>
        <div className="fu-onboard-block" style={{ marginTop: 10 }}>
          <div className="fu-onboard-block-label">{t('followups.custom.sequence', 'Secuencia')}</div>
          {STEP_KEYS.map((key) => (
            <div key={key} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12.5 }}>
              <span style={{ fontWeight: 600, color: '#0A0A0A' }}>{stepName(key, t)}</span>
              <span style={{ color: '#9A9A9A' }}>{steps[key].active ? stepWhen(key) : t('followups.stepPill.inactive', 'Inactivo')}</span>
            </div>
          ))}
        </div>
        <div className="fu-onboard-check"><Check size={15} /> {t('followups.custom.check.emailsReady', `${STEP_KEYS.filter((k) => steps[k].active).length} correos preparados`).replace('{n}', STEP_KEYS.filter((k) => steps[k].active).length)}</div>
        <div className="fu-onboard-explain">{t('followups.custom.step4.explain', 'La secuencia se detendrá automáticamente cuando alguien responda.')}</div>
      </>
    );
  };

  return (
    <div className="fu-wrap">
      <style>{CSS}</style>
      <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleAttachmentChange} />

      {onboardPhase === 'landing' && (
        <div className="fu-modal-backdrop">
          <div className="fu-modal-card fu-modal-card--wide">
            <div className="fu-onboard-top">
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div className="fu-onboard-h1">{t('followups.landing.title', 'Configura tus Follow-ups')}</div>
                <div className="fu-onboard-h1-sub" style={{ margin: '6px auto 0' }}>{t('followups.landing.sub', 'Haz seguimiento automáticamente después de cada nueva conexión.')}</div>
              </div>
              <button className="fu-onboard-skip" style={{ flexShrink: 0 }} onClick={() => setOnboardPhase(null)}>{t('followups.finishLater', 'Terminar más tarde')}</button>
            </div>
            <div className="fu-onboard-flow">
              <span>{stepName('connection', t)}</span><ChevronRight size={14} className="fu-onboard-flow-arrow" />
              <span>{stepName('followup', t)}</span><ChevronRight size={14} className="fu-onboard-flow-arrow" />
              <span>{stepName('value', t)}</span>
            </div>
            <div className="fu-onboard-flow-note">{t('followups.landing.flowNote', 'Si una persona responde, CONTACT detiene automáticamente los siguientes correos.')}</div>
            <div className="fu-path-grid">
              <div className="fu-path-card fu-path-card--reco">
                <span className="fu-path-badge">{t('followups.landing.recommended', 'Recomendado')}</span>
                <div className="fu-path-title">{t('followups.landing.recoTitle', 'Empieza en menos de un minuto')}</div>
                <div className="fu-path-desc">{t('followups.landing.recoDesc', 'Usa una secuencia preparada por CONTACT y empieza a hacer seguimiento sin configurar todo desde cero.')}</div>
                <div className="fu-path-visual">
                  <div className="fu-path-visual-step"><span className="fu-path-visual-name">Email 1</span><span className="fu-path-visual-when">{t('followups.delay.0', 'Inmediatamente')}</span></div>
                  <div className="fu-path-visual-step"><span className="fu-path-visual-name">Email 2</span><span className="fu-path-visual-when">{t('followups.landing.day3', 'Día 3 si no responde')}</span></div>
                  <div className="fu-path-visual-step"><span className="fu-path-visual-name">Email 3</span><span className="fu-path-visual-when">{t('followups.landing.day6', 'Día 6 si no responde')}</span></div>
                </div>
                <button className="fu-btn fu-btn-primary fu-path-cta" style={{ justifyContent: 'center' }} onClick={startRecommendedOnboarding}>{t('followups.landing.useRecommended', 'Usar configuración recomendada')}</button>
              </div>
              <div className="fu-path-card">
                <div className="fu-path-title">{t('followups.landing.customTitle', 'Configúralo a tu manera')}</div>
                <div className="fu-path-desc">{t('followups.landing.customDesc', 'Personaliza los tiempos y mensajes antes de activar tu seguimiento.')}</div>
                <button className="fu-btn fu-btn-secondary fu-path-cta" style={{ justifyContent: 'center' }} onClick={startCustomOnboarding}>{t('followups.landing.customCta', 'Personalizar Follow-ups')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {onboardPhase === 'recommended' && (
        <div className="fu-modal-backdrop">
          <div className="fu-modal-card">
            <div className="fu-onboard-title">{t('followups.reco.title', 'Tu Follow-up está listo')}</div>
            <div className="fu-onboard-block">
              <div className="fu-onboard-block-label">{t('followups.reco.identity', 'Identidad')}</div>
              <div className="fu-onboard-block-value">{user?.name || t('followups.preview.yourName', 'Tu nombre')}</div>
              <div style={{ fontSize: 12.5, color: '#6B6B6B', marginTop: 6 }}>{t('followups.custom.sentAs', 'Tus correos se enviarán como')}</div>
              <div style={{ fontSize: 12.5, color: '#0A0A0A', fontWeight: 600 }}>{user?.name || t('followups.preview.you', 'Tú')} · {senderEmail || '—'}</div>
            </div>
            <div className="fu-onboard-block">
              <div className="fu-onboard-block-label">{t('followups.custom.repliesTo', 'Las respuestas llegarán directamente a')}</div>
              <input
                className="fu-input"
                style={{ marginTop: 4 }}
                value={replyToEmail}
                onChange={(e) => { setReplyToEmail(e.target.value); setDirty(true); }}
                placeholder={user?.email || t('followups.custom.emailPlaceholder', 'tu@correo.com')}
              />
            </div>
            {STEP_KEYS.map((key) => (
              <div className="fu-onboard-mini-step" key={key}>
                <div>
                  <div className="fu-onboard-mini-name">{stepName(key, t)}</div>
                  <div className="fu-onboard-mini-when">{stepWhen(key)}</div>
                </div>
              </div>
            ))}
            <div className="fu-onboard-block">
              <div className="fu-onboard-block-label">{t('followups.reco.messages', 'Mensajes')}</div>
              <div className="fu-onboard-block-value" style={{ fontSize: 12.5, fontWeight: 500 }}>{t('followups.reco.messagesReady', '3 mensajes recomendados preparados')}</div>
            </div>
            <div className="fu-onboard-explain" style={{ marginTop: 4 }}>{t('followups.reco.explain', 'Los próximos correos se cancelarán automáticamente cuando una persona responda.')}</div>
            <div className="fu-onboard-actions">
              <button className="fu-btn fu-btn-primary" style={{ justifyContent: 'center' }} onClick={activateFromOnboarding}>{t('followups.reco.activate', 'Activar Follow-ups')}</button>
              <button className="fu-btn fu-btn-secondary" style={{ justifyContent: 'center' }} onClick={handleSendTest}>{t('followups.reco.sendTest', 'Enviar una prueba')}</button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button className="fu-onboard-skip" onClick={() => setOnboardPhase('landing')}>{t('followups.back', '← Volver')}</button>
              <button className="fu-onboard-skip" onClick={() => setOnboardPhase(null)}>{t('followups.finishLater', 'Terminar más tarde')}</button>
            </div>
          </div>
        </div>
      )}

      {onboardPhase === 'custom' && (
        <div className="fu-modal-backdrop">
          <div className="fu-modal-card fu-modal-card--wide">
            <div className="fu-onboard-top">
              <div>
                <div className="fu-onboard-eyebrow">{t('followups.custom.stepOf', `Paso ${onboardCustomStep} de 4`).replace('{n}', onboardCustomStep)}</div>
                <div className="fu-onboard-h1">{customStepTitles[onboardCustomStep - 1]}</div>
                <div className="fu-onboard-h1-sub">{customStepSubs[onboardCustomStep - 1]}</div>
              </div>
              <button className="fu-onboard-skip" style={{ flexShrink: 0 }} onClick={() => setOnboardPhase(null)}>{t('followups.finishLater', 'Terminar más tarde')}</button>
            </div>
            <div className="fu-progress-steps">
              {[1, 2, 3, 4].map((n) => <span key={n} className={`fu-progress-seg ${n <= onboardCustomStep ? 'fu-progress-seg--done' : ''}`} />)}
            </div>
            <div className="fu-wizard-body">{renderCustomStep()}</div>
            <div className="fu-wizard-footer">
              <div>
                {onboardCustomStep > 1 && <button className="fu-btn fu-btn-secondary" onClick={handleOnboardCustomBack}><ChevronLeft size={14} /> {t('followups.custom.back', 'Atrás')}</button>}
              </div>
              {onboardCustomStep < 4 ? (
                <button className="fu-btn fu-btn-primary" onClick={handleOnboardCustomNext}>{t('followups.custom.continue', 'Continuar')}</button>
              ) : (
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="fu-btn fu-btn-secondary" onClick={handleSendTest}>{t('followups.reco.sendTest', 'Enviar una prueba')}</button>
                  <button className="fu-btn fu-btn-primary" onClick={activateFromOnboarding}>{t('followups.reco.activate', 'Activar Follow-ups')}</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {onboardPhase === 'success' && (
        <div className="fu-modal-backdrop" onClick={() => setOnboardPhase(null)}>
          <div className="fu-modal-card fu-modal-card--center" onClick={(e) => e.stopPropagation()}>
            <div className="fu-success-icon"><Check size={28} /></div>
            <div className="fu-onboard-title">{t('followups.success.title', 'Follow-ups está activo')}</div>
            <div className="fu-onboard-sub">{t('followups.success.sub', 'Tu próxima conexión recibirá seguimiento automáticamente.')}</div>
            <div className="fu-onboard-flow" style={{ marginTop: 0 }}>
              <span>{stepName('connection', t)}</span><ChevronRight size={14} className="fu-onboard-flow-arrow" />
              <span>{stepName('followup', t)}</span><ChevronRight size={14} className="fu-onboard-flow-arrow" />
              <span>{stepName('value', t)}</span>
            </div>
            <button className="fu-btn fu-btn-primary" style={{ justifyContent: 'center', width: '100%', marginTop: 16 }} onClick={() => setOnboardPhase(null)}>{t('followups.success.goTo', 'Ir a Follow-ups')}</button>
          </div>
        </div>
      )}

      {howItWorksOpen && (
        <div className="fu-modal-backdrop" onClick={() => setHowItWorksOpen(false)}>
          <div className="fu-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fu-onboard-title" style={{ marginBottom: 20 }}>{t('followups.howItWorks.title', 'Cómo funciona')}</div>
            <div className="fu-howit-item">
              <div className="fu-howit-num">1</div>
              <div><div className="fu-howit-title">{t('followups.howItWorks.step1.title', 'Nueva conexión')}</div><div className="fu-howit-desc">{t('followups.howItWorks.step1.desc', 'Una persona comparte sus datos contigo.')}</div></div>
            </div>
            <div className="fu-howit-item">
              <div className="fu-howit-num">2</div>
              <div><div className="fu-howit-title">{t('followups.howItWorks.step2.title', 'Seguimiento automático')}</div><div className="fu-howit-desc">{t('followups.howItWorks.step2.desc', 'CONTACT envía tu secuencia según los tiempos configurados.')}</div></div>
            </div>
            <div className="fu-howit-item" style={{ marginBottom: 8 }}>
              <div className="fu-howit-num">3</div>
              <div><div className="fu-howit-title">{t('followups.howItWorks.step3.title', 'Se detiene al responder')}</div><div className="fu-howit-desc">{t('followups.howItWorks.step3.desc', 'Si la persona responde, los siguientes emails se cancelan automáticamente.')}</div></div>
            </div>
            <div className="fu-howit-note">{t('followups.howItWorks.note', 'Follow-ups se inicia cuando se genera una conexión real (formulario público o scan) — no simplemente porque alguien vea tu perfil o abra un link.')}</div>
            <button className="fu-btn fu-btn-primary" style={{ justifyContent: 'center', width: '100%', marginTop: 20 }} onClick={() => setHowItWorksOpen(false)}>{t('followups.howItWorks.understood', 'Entendido')}</button>
          </div>
        </div>
      )}

      {drawerItem && (
        <SequenceDrawer
          item={drawerItem}
          onClose={() => setDrawerItem(null)}
          onStop={() => handleStopSequence(drawerItem)}
        />
      )}

      <div className="fu-header">
        <div className="fu-header-left">
          <div className="fu-header-icon"><Send size={19} /></div>
          <div>
            <div className="fu-title-row">
              <span className="fu-title">Follow-ups</span>
              <button className="fu-help-btn" onClick={() => setHowItWorksOpen(true)}>
                <HelpCircle size={13} /> {t('followups.howItWorks.title', 'Cómo funciona')}
              </button>
            </div>
            <div className="fu-sub">{t('followups.header.sub', 'Automatiza el seguimiento después de cada nueva conexión.')}</div>
          </div>
        </div>
      </div>

      <div className="fu-toolbar">
        <div className="fu-tabs">
          <button className={`fu-tab ${view === 'sequence' ? 'fu-tab--active' : ''}`} onClick={() => setView('sequence')}>{t('followups.tabs.sequence', 'Secuencia')}</button>
          <button className={`fu-tab ${view === 'activity' ? 'fu-tab--active' : ''}`} onClick={() => setView('activity')}>{t('followups.tabs.activity', 'Actividad')}</button>
        </div>
        <button
          className={`fu-status-pill ${active ? 'fu-status-pill--on' : 'fu-status-pill--off'}`}
          onClick={handleToggleActive}
          disabled={!configured}
          title={configured ? '' : t('followups.notConfiguredTitle', 'Follow-ups no está configurado todavía')}
        >
          <span className="fu-status-dot" />
          {active ? t('followups.stepPill.active', 'Activo') : t('followups.paused', 'Pausado')}
        </button>
      </div>

      {!configured && (
        <div className="fu-card" style={{ marginBottom: 24, borderColor: 'rgba(217,119,6,.3)', background: 'rgba(217,119,6,.05)' }}>
          <span style={{ fontSize: 13, color: '#D97706' }}>{t('followups.notConfiguredBanner', 'Follow-ups todavía no está configurado en la plataforma. Contáctanos para activarlo.')}</span>
        </div>
      )}

      {view === 'activity' ? (
        <ActivityView authHeaders={authHeaders} onOpenItem={setDrawerItem} toast={toast} />
      ) : (
        <>
          <div className="fu-sub" style={{ marginBottom: 10, fontSize: 12, color: active ? '#16A34A' : '#9A9A9A' }}>
            {active ? t('followups.activeNote', 'Las nuevas conexiones están entrando automáticamente a esta secuencia.') : t('followups.pausedNote', 'Las nuevas conexiones no recibirán seguimiento automático.')}
          </div>

          {showReplyWarning && (
            <div className="fu-warning-banner">
              <AlertTriangle size={16} />
              <span>{t('followups.replyWarning', 'Email 2 y/o Email 3 están activados, pero se enviarán en cuanto terminemos de habilitar la detección de respuestas. Mientras tanto quedan guardados, sin enviarse.')}</span>
            </div>
          )}

          <div className="fu-metrics-row">
            <div className="fu-metric-card"><div className="fu-metric-value">{metrics.iniciadas}</div><div className="fu-metric-label">{t('followups.metric.started', 'Seguimientos iniciados')}</div></div>
            <div className="fu-metric-card"><div className="fu-metric-value">{metrics.respuestas}</div><div className="fu-metric-label">{t('followups.metric.responded', 'Respondieron')}</div></div>
            <div className="fu-metric-card"><div className="fu-metric-value">{responseRateLabel}</div><div className="fu-metric-label">{t('followups.metric.responseRate', 'Tasa de respuesta')}</div></div>
            <div className="fu-metric-card"><div className="fu-metric-value">{metrics.en_seguimiento}</div><div className="fu-metric-label">{t('followups.metric.active', 'En seguimiento')}</div></div>
          </div>

          <div className="fu-sender-card">
            <div className="fu-sender-left">
              <div className="fu-sender-icon"><Mail size={18} /></div>
              <div>
                <div className="fu-sender-label">
                  {t('followups.sender.identity', 'Identidad de envío')}
                  <span className="fu-info-tip">
                    <Info size={12} />
                    <span className="fu-tip-bubble">{t('followups.sender.identityTip', 'Tus contactos verán tu nombre y sus respuestas llegarán directamente a este correo.')}</span>
                  </span>
                </div>
                <div className="fu-sender-name">{user?.name || t('followups.preview.you', 'Tú')}</div>
                <div className="fu-sender-addr">{senderEmail || '—'}</div>
              </div>
            </div>
            <div className="fu-sender-reply">
              <div>
                <div className="fu-sender-label" style={{ marginBottom: 4 }}>{t('followups.custom.repliesTo', 'Las respuestas llegarán a')}</div>
                <input
                  className="fu-input"
                  value={replyToEmail}
                  onChange={(e) => { setReplyToEmail(e.target.value); setDirty(true); }}
                  placeholder={user?.email || t('followups.custom.emailPlaceholder', 'tu@correo.com')}
                />
              </div>
            </div>
          </div>

          <div className="fu-timeline-card">
            <div className="fu-timeline-title">{t('followups.timeline.title', 'Tu secuencia')}</div>
            <div className="fu-timeline">
              {STEP_KEYS.map((key, i) => {
                const st = stepStatusPill(key);
                return (
                  <React.Fragment key={key}>
                    <button className={`fu-timeline-step ${selectedStep === key ? 'fu-timeline-step--active' : ''}`} onClick={() => setSelectedStep(key)}>
                      <span className="fu-timeline-num">{STEP_META[key].num}</span>
                      <span className="fu-timeline-name">{stepName(key, t)}</span>
                      <span className="fu-timeline-when">{stepWhen(key)}</span>
                      <span className={`fu-step-status ${st.cls}`}>{st.text}</span>
                    </button>
                    {i < STEP_KEYS.length - 1 && <div className="fu-timeline-connector"><ChevronRight size={18} /></div>}
                  </React.Fragment>
                );
              })}
            </div>
            <div className="fu-timeline-foot">{t('followups.timeline.foot', 'La secuencia se detiene automáticamente cuando el contacto responde.')}</div>
          </div>

          <div className="fu-grid">
            <div className="fu-card">
              <div className="fu-card-title-row">
                <div className="fu-card-title">{t('followups.edit.title', `Editar ${STEP_META[selectedStep].name}`).replace('{step}', stepName(selectedStep, t))}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className={`fu-save-state ${dirty ? 'fu-save-state--dirty' : 'fu-save-state--saved'}`}>
                    {dirty ? t('followups.edit.unsaved', 'Cambios sin guardar') : (<><Check size={13} /> {t('followups.edit.saved', 'Guardado')}</>)}
                  </span>
                  <button
                    className={`fu-toggle fu-toggle-sm ${currentStep.active ? 'fu-toggle--on' : 'fu-toggle--off'}`}
                    onClick={() => handleStepActiveToggle(selectedStep)}
                  >
                    <span className="fu-toggle-thumb" />
                  </button>
                </div>
              </div>
              <div className="fu-card-desc">
                {selectedStep === 'connection' && t('followups.edit.desc.connection', 'Se envía tras la conexión, con el tiempo de espera que elijas.')}
                {selectedStep === 'followup' && t('followups.edit.desc.followup', 'Se envía solo si la persona no te ha respondido todavía.')}
                {selectedStep === 'value' && t('followups.edit.desc.value', 'Cierra la secuencia — podés adjuntar un archivo o compartir un link.')}
              </div>

              {selectedStep !== 'connection' && !replyDetectionConfigured && (
                <div className="fu-warning-banner" style={{ marginBottom: 22 }}>
                  <AlertTriangle size={15} />
                  <span>{t('followups.edit.pendingReplyConfig', 'Se activará automáticamente cuando completemos la configuración de detección de respuestas.')}</span>
                </div>
              )}

              <div className="fu-field">
                <label className="fu-label">{selectedStep === 'connection' ? t('followups.edit.sendAfter', 'Enviar después de') : t('followups.edit.sendOnDay', 'Enviar el día (desde el Email 1)')}</label>
                {selectedStep === 'connection' ? (
                  <select className="fu-select" value={delayMinutes} onChange={(e) => { setDelayMinutes(Number(e.target.value)); setDirty(true); }}>
                    {Object.keys(DELAY_LABELS).map((val) => (
                      <option key={val} value={val}>{delayLabel(Number(val), t)}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="number" min={1} max={30} className="fu-input"
                    value={selectedStep === 'followup' ? step2Days : step3Days}
                    onChange={(e) => {
                      if (selectedStep === 'followup') setStep2Days(Number(e.target.value)); else setStep3Days(Number(e.target.value));
                      setDirty(true);
                    }}
                  />
                )}
              </div>

              <div className="fu-field">
                <label className="fu-label">{t('followups.field.subject', 'Asunto')}</label>
                <input
                  ref={subjectRef}
                  className="fu-input"
                  value={currentStep.subject}
                  onChange={(e) => handleStepField(selectedStep, 'subject', e.target.value)}
                  placeholder={t('followups.field.subjectPlaceholder', 'Un gusto conocerte, {{nombre}}')}
                />
                <div className="fu-chips">
                  {activeStepVars.map((v) => (
                    <button key={v.token} className="fu-chip" onClick={() => insertVariable(subjectRef, selectedStep, 'subject', v.token)}>+ {v.label}</button>
                  ))}
                </div>
              </div>

              <div className="fu-field">
                <label className="fu-label">{t('followups.field.message', 'Mensaje')}</label>
                <textarea
                  ref={messageRef}
                  className="fu-textarea"
                  value={currentStep.message}
                  onChange={(e) => handleStepField(selectedStep, 'message', e.target.value)}
                />
                <div className="fu-chips">
                  {activeStepVars.map((v) => (
                    <button key={v.token} className="fu-chip" onClick={() => insertVariable(messageRef, selectedStep, 'message', v.token)}>+ {v.label}</button>
                  ))}
                </div>
                <button className="fu-restore-link" onClick={() => handleRestoreDefault(selectedStep)}>
                  <RotateCcw size={12} /> {t('followups.restoreLink', 'Restaurar mensaje recomendado')}
                </button>
              </div>

              {selectedStep === 'value' && (
                <div className="fu-field">
                  <label className="fu-label">{t('followups.field.attachment', 'Adjunto o recurso')}</label>
                  {currentStep.attachment ? (
                    <div className="fu-attach-chip">
                      <Paperclip size={15} color="#6B6B6B" />
                      <span className="fu-attach-chip-name">{currentStep.attachment.filename}</span>
                      <span className="fu-attach-chip-size">{formatBytes(currentStep.attachment.size_bytes)}</span>
                      <button className="fu-attach-remove" onClick={handleAttachmentRemove} disabled={uploading}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="fu-attach-box" onClick={handleAttachmentPick}>
                        {uploading ? <Loader2 size={16} className="fu-spin" /> : <Paperclip size={16} color="#9A9A9A" />}
                        <div className="fu-attach-box-text">{uploading ? t('followups.uploading', 'Subiendo…') : t('followups.attachHint', 'Hacé clic para adjuntar un archivo (máx. 8MB)')}</div>
                      </div>
                      <div className="fu-or-divider">{t('followups.or', 'o')}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Link2 size={15} color="#9A9A9A" />
                        <input
                          className="fu-input"
                          value={currentStep.resource_link}
                          onChange={(e) => handleStepField('value', 'resource_link', e.target.value)}
                          placeholder={t('followups.field.resourceLinkPlaceholder', 'https://tu-recurso.com')}
                        />
                      </div>
                    </>
                  )}
                  <div className="fu-chips">
                    <button className="fu-chip" onClick={() => insertVariable(messageRef, 'value', 'message', 'recurso')}>+ {t('followups.field.valueResource', 'Recurso de valor')}</button>
                  </div>
                </div>
              )}

              <div className="fu-actions">
                <button className="fu-btn fu-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 size={14} className="fu-spin" /> : null} {t('followups.saveChanges', 'Guardar cambios')}
                </button>
                <select className="fu-test-select" value={testStep} onChange={(e) => setTestStep(e.target.value)}>
                  {STEP_KEYS.map((key) => <option key={key} value={key}>{stepName(key, t)}</option>)}
                </select>
                <button
                  className={`fu-btn fu-btn-secondary ${testState === 'sent' ? 'fu-btn--ok' : ''} ${testState === 'error' ? 'fu-btn--err' : ''}`}
                  onClick={handleSendTest}
                  disabled={testState === 'sending' || !configured}
                >
                  {testState === 'sending' && <>{t('followups.sending', 'Enviando…')}</>}
                  {testState === 'sent' && <><Check size={14} /> {t('followups.testSentBtn', 'Prueba enviada')}</>}
                  {testState === 'error' && <><X size={14} /> {t('followups.testErrorBtn', 'No se pudo enviar')}</>}
                  {testState === 'idle' && <>{t('followups.sendTestBtn', 'Enviar prueba')}</>}
                </button>
              </div>
            </div>

            <div className="fu-card">
              <div className="fu-card-title">{t('followups.preview.title', 'Vista previa')}</div>
              <div className="fu-card-desc">{t('followups.preview.desc', 'Con datos de ejemplo, así se vería en la bandeja de tu contacto.')}</div>
              <div className="fu-preview">
                <div className="fu-preview-meta">
                  <div><b>{t('followups.preview.from', 'De:')}</b> {renderPreview('{{mi_nombre_completo}}', previewValues)} {senderEmail ? `<${senderEmail}>` : ''}</div>
                  <div><b>{t('followups.preview.to', 'Para:')}</b> Martín Pérez</div>
                  <div><b>{t('followups.preview.subject', 'Asunto:')}</b> {renderPreview(currentStep.subject, previewValues)}</div>
                </div>
                <div className="fu-preview-body">{renderPreview(currentStep.message, previewValues)}</div>
                {selectedStep === 'value' && (currentStep.attachment || currentStep.resource_link) && (
                  <div className="fu-preview-attach">
                    <Paperclip size={13} />
                    {currentStep.attachment ? currentStep.attachment.filename : currentStep.resource_link}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="fu-card">
            <div className="fu-card-title">{t('followups.recent.title', 'Actividad reciente')}</div>
            <div className="fu-card-desc">{t('followups.recent.desc', 'Qué ha pasado con cada secuencia enviada.')}</div>
            {recentLoading ? null : recentActivity.length === 0 ? (
              <div className="fu-empty">
                <div className="fu-empty-icon"><Inbox size={18} /></div>
                <div className="fu-empty-title">{t('followups.empty.title', 'Aún no has iniciado ningún seguimiento')}</div>
                <div className="fu-empty-text">{t('followups.empty.text', 'Cuando captures tu próxima conexión, comenzará automáticamente tu secuencia.')}</div>
              </div>
            ) : (
              <>
                {recentActivity.map((item) => (
                  <div className="fu-recent-row" key={item.id} onClick={() => setDrawerItem(item)}>
                    <div>
                      <div className="fu-recent-name">{item.full_name || item.email}</div>
                      <div className="fu-recent-summary">{item.summary || sequenceStatusLabel(normalizedStatus(item.follow_up?.status), t)}</div>
                    </div>
                    <div className="fu-recent-time">{timeAgo(lastActivityAt(item.follow_up), t)}</div>
                  </div>
                ))}
                <button className="fu-view-all" onClick={() => setView('activity')}>
                  {t('followups.viewAllActivity', 'Ver toda la actividad')} <ChevronRight size={14} />
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────── */

const ActivityView = ({ authHeaders, onOpenItem, toast }) => {
  const { t, lang } = useLanguage();
  const dateLocale = lang === 'en' ? 'en-US' : 'es-CL';
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loadingList, setLoadingList] = useState(true);

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => { setPage(0); }, [debouncedSearch, statusFilter]);

  useEffect(() => {
    let cancelled = false;
    setLoadingList(true);
    const params = new URLSearchParams({
      limit: String(PAGE_SIZE),
      skip: String(page * PAGE_SIZE),
    });
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (statusFilter) params.set('status', statusFilter);
    axios.get(`${API}/follow-ups/activity?${params.toString()}`, { headers: authHeaders() })
      .then((res) => {
        if (cancelled) return;
        setItems(res.data?.items || []);
        setTotal(res.data?.total || 0);
      })
      .catch(() => { if (!cancelled) toast?.error?.(t('followups.activity.loadError', 'No pudimos cargar la actividad.')); })
      .finally(() => { if (!cancelled) setLoadingList(false); });
    return () => { cancelled = true; };
  }, [page, debouncedSearch, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <div>
      <div className="fu-activity-toolbar">
        <div className="fu-search-box">
          <Search size={15} />
          <input
            className="fu-search-input"
            placeholder={t('followups.activity.searchPlaceholder', 'Buscar contacto...')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="fu-filters">
          {ACTIVITY_FILTERS.map((f) => (
            <button
              key={f.key}
              className={`fu-filter-pill ${statusFilter === f.key ? 'fu-filter-pill--active' : ''}`}
              onClick={() => setStatusFilter(f.key)}
            >
              {activityFilterLabel(f, t)}
            </button>
          ))}
        </div>
      </div>

      <div className="fu-card">
        {loadingList ? (
          <div className="fu-loading"><Loader2 size={20} className="fu-spin" /></div>
        ) : items.length === 0 ? (
          <div className="fu-empty">
            <div className="fu-empty-icon"><Inbox size={18} /></div>
            {debouncedSearch ? (
              <>
                <div className="fu-empty-title">{t('followups.activity.noSearchResults', 'Sin resultados de búsqueda')}</div>
                <div className="fu-empty-text">{t('followups.activity.noSearchResultsText', `No encontramos contactos para "${debouncedSearch}".`).replace('{query}', debouncedSearch)}</div>
              </>
            ) : statusFilter ? (
              <>
                <div className="fu-empty-title">{t('followups.activity.noResults', 'Sin resultados')}</div>
                <div className="fu-empty-text">{t('followups.activity.noResultsText', 'No encontramos seguimientos con este estado.')}</div>
              </>
            ) : (
              <>
                <div className="fu-empty-title">{t('followups.empty.title', 'Aún no has iniciado ningún seguimiento')}</div>
                <div className="fu-empty-text">{t('followups.empty.text', 'Cuando captures tu próxima conexión, comenzará automáticamente tu secuencia.')}</div>
              </>
            )}
          </div>
        ) : (
          <>
            <table className="fu-table">
              <thead>
                <tr>
                  <th>{t('followups.table.contact', 'Contacto')}</th>
                  <th>{t('followups.table.status', 'Estado')}</th>
                  <th>{t('followups.table.progress', 'Progreso')}</th>
                  <th>{t('followups.table.nextStep', 'Próximo paso')}</th>
                  <th>{t('followups.table.lastActivity', 'Última actividad')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const status = normalizedStatus(item.follow_up?.status);
                  return (
                    <tr key={item.id} onClick={() => onOpenItem(item)}>
                      <td>
                        <div className="fu-table-name">{item.full_name || '—'}</div>
                        <div className="fu-table-email">{item.email}</div>
                      </td>
                      <td><span className={`fu-activity-badge ${SEQUENCE_BADGE_CLASS[status] || 'fu-badge--scheduled'}`}>{sequenceStatusLabel(status, t)}</span></td>
                      <td className="fu-table-muted">{stepProgress(item.follow_up?.steps, t)}</td>
                      <td className="fu-table-muted">{status === 'active' ? nextStepInfo(item.follow_up?.steps, t, dateLocale) : '—'}</td>
                      <td className="fu-table-muted">{fullDateTime(lastActivityAt(item.follow_up), dateLocale)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="fu-pagination">
              <div className="fu-pagination-info">{t('followups.pagination.range', `${rangeStart}–${rangeEnd} de ${total}`).replace('{start}', rangeStart).replace('{end}', rangeEnd).replace('{total}', total)}</div>
              <div className="fu-pagination-btns">
                <button className="fu-page-btn" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
                  <ChevronLeft size={14} /> {t('followups.pagination.prev', 'Anterior')}
                </button>
                <button className="fu-page-btn" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
                  {t('followups.pagination.next', 'Siguiente')} <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────── */

const SequenceDrawer = ({ item, onClose, onStop }) => {
  const { t, lang } = useLanguage();
  const dateLocale = lang === 'en' ? 'en-US' : 'es-CL';
  const followUp = item.follow_up || {};
  const status = normalizedStatus(followUp.status);
  const steps = followUp.steps || {};

  const stepNote = (key, step) => {
    if (step.status === 'cancelled' && status === 'responded') return t('followups.drawer.note.respondedBefore', 'Respondió antes del envío');
    if (step.status === 'cancelled') return t('followups.drawer.note.stopped', 'Seguimiento detenido');
    if (step.status === 'skipped') return t('followups.drawer.note.wasNotActive', 'No estaba activo');
    if (step.status === 'failed') return t('followups.drawer.note.failed', 'No se pudo entregar');
    if (step.status === 'bounced') return t('followups.drawer.note.bounced', 'El correo rebotó');
    return '';
  };

  const nextKey = STEP_KEYS.find((k) => steps[k]?.status === 'scheduled');

  return (
    <>
      <div className="fu-drawer-backdrop" onClick={onClose} />
      <div className="fu-drawer">
        <div className="fu-drawer-head">
          <div>
            <div className="fu-drawer-name">{item.full_name || '—'}</div>
            <div className="fu-drawer-email">{item.email}</div>
          </div>
          <button className="fu-drawer-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="fu-drawer-body">
          <div className="fu-drawer-status-label">{t('followups.table.status', 'Estado')}</div>
          <span className={`fu-activity-badge ${SEQUENCE_BADGE_CLASS[status] || 'fu-badge--scheduled'}`} style={{ fontSize: 12.5 }}>
            {sequenceStatusLabel(status, t)}
          </span>

          <div className="fu-drawer-steps">
            {STEP_KEYS.map((key) => {
              const step = steps[key];
              if (!step) return null;
              const done = ['sent', 'delivered'].includes(step.status);
              const cancelled = ['cancelled', 'failed', 'bounced'].includes(step.status);
              return (
                <div key={key} className={`fu-drawer-step ${done ? 'fu-drawer-step--done' : ''} ${cancelled ? 'fu-drawer-step--cancelled' : ''}`}>
                  <div className="fu-drawer-step-name">{stepName(key, t)}</div>
                  <div className="fu-drawer-step-status">
                    {done ? `${stepStatusLabel(step.status, t)} · ${fullDateTime(step.updated_at || step.scheduled_at, dateLocale)}` : stepStatusLabel(step.status, t)}
                  </div>
                  {stepNote(key, step) && <div className="fu-drawer-step-note">{stepNote(key, step)}</div>}
                </div>
              );
            })}
            {status === 'responded' && followUp.responded_at && (
              <div className="fu-drawer-step fu-drawer-step--done">
                <div className="fu-drawer-step-name">{t('followups.drawer.responseReceived', 'Respuesta recibida')}</div>
                <div className="fu-drawer-step-status">{fullDateTime(followUp.responded_at, dateLocale)}</div>
              </div>
            )}
          </div>

          {status === 'active' && nextKey && (
            <div className="fu-drawer-next">
              <div className="fu-drawer-next-label">{t('followups.drawer.nextSend', 'Próximo envío')}</div>
              <div className="fu-drawer-next-value">{stepName(nextKey, t)}</div>
              <div className="fu-drawer-next-time">{fullDateTime(steps[nextKey].scheduled_at, dateLocale)}</div>
            </div>
          )}
        </div>
        {status === 'active' && (
          <div className="fu-drawer-foot">
            <button className="fu-btn-danger-text" onClick={onStop} style={{ width: '100%', justifyContent: 'center', display: 'flex' }}>
              {t('followups.drawer.stopSequence', 'Detener seguimiento')}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default FollowUpsPanel;
